import { useEffect, useState } from 'react';
import { useStandalonePwa } from './useStandalonePwa';

const MOBILE_MAX_WIDTH = 1024;

// Matches MobileHeader: inline `unset` overrides `body { overflow: hidden }`
// in ProjectDashboard.css so the dashboard can scroll.
const RESTORED_BODY_OVERFLOW = 'unset';

let openFullscreenSheetModalCount = 0;
let lockedScrollY = 0;
let restoreRafId = 0;

function isPwaShell() {
  if (typeof document === 'undefined') {
    return false;
  }

  const { classList } = document.documentElement;
  return (
    classList.contains('nettles-pwa-has-bottom-nav')
    || classList.contains('nettles-pwa-app')
    || classList.contains('nettles-pwa')
  );
}

function syncVisualViewportHeight() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const height = window.visualViewport?.height || window.innerHeight || 0;
  if (height > 0) {
    document.documentElement.style.setProperty('--nettles-vvh', `${Math.round(height)}px`);
  }
}

export function restoreDashboardBodyScroll() {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.classList.remove('nettles-pwa-sheet-modal-open');
  document.documentElement.style.removeProperty('--nettles-vvh');

  const { body } = document;
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  body.style.overflow = RESTORED_BODY_OVERFLOW;
}

function scheduleRestoreIfIdle() {
  restoreDashboardBodyScroll();

  if (typeof window === 'undefined') {
    return;
  }

  if (restoreRafId) {
    window.cancelAnimationFrame(restoreRafId);
  }

  const restoreIfIdle = () => {
    if (openFullscreenSheetModalCount !== 0) {
      return;
    }
    restoreDashboardBodyScroll();
    window.scrollTo(0, lockedScrollY);
  };

  restoreRafId = window.requestAnimationFrame(() => {
    restoreRafId = 0;
    restoreIfIdle();
    window.requestAnimationFrame(restoreIfIdle);
  });
  window.setTimeout(restoreIfIdle, 0);
}

function lockPwaChromeForSheetModal() {
  if (typeof document === 'undefined') {
    return;
  }

  openFullscreenSheetModalCount += 1;
  if (openFullscreenSheetModalCount !== 1) {
    syncVisualViewportHeight();
    return;
  }

  if (restoreRafId && typeof window !== 'undefined') {
    window.cancelAnimationFrame(restoreRafId);
    restoreRafId = 0;
  }

  document.documentElement.classList.add('nettles-pwa-sheet-modal-open');
  syncVisualViewportHeight();

  const { body } = document;
  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  body.style.overflow = 'hidden';
}

function unlockPwaChromeForSheetModal() {
  if (typeof document === 'undefined') {
    return;
  }

  openFullscreenSheetModalCount = Math.max(0, openFullscreenSheetModalCount - 1);
  if (openFullscreenSheetModalCount !== 0) {
    return;
  }

  scheduleRestoreIfIdle();
  lockedScrollY = 0;
}

/** Clear leftover scroll lock after the last flow modal closes. */
export function forceUnlockAllSheetModals() {
  if (typeof document === 'undefined') {
    return;
  }

  openFullscreenSheetModalCount = 0;
  scheduleRestoreIfIdle();
  lockedScrollY = 0;
}

export function shouldUseFullscreenSheetModal(isStandalonePwa = false) {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    isPwaShell()
    || isStandalonePwa
    || window.innerWidth <= MOBILE_MAX_WIDTH
  );
}

/**
 * @param {boolean} isOpen
 * @param {{ skipLock?: boolean }} [options] skipLock for nested Equipment sheet
 *   (Crew Timesheet already holds the chrome lock).
 */
export function useFullscreenSheetModal(isOpen, options = {}) {
  const skipLock = Boolean(options.skipLock);
  const isStandalonePwa = useStandalonePwa();
  const [, setLayoutVersion] = useState(0);

  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;

  const isFullscreenModal = isOpen && (
    shouldUseFullscreenSheetModal(isStandalonePwa) || isMobileViewport
  );

  useEffect(() => {
    if (!isOpen || skipLock) {
      return undefined;
    }

    lockPwaChromeForSheetModal();

    const sync = () => {
      syncVisualViewportHeight();
      setLayoutVersion((value) => value + 1);
    };

    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    window.visualViewport?.addEventListener('resize', sync);
    window.visualViewport?.addEventListener('scroll', sync);

    return () => {
      unlockPwaChromeForSheetModal();
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      window.visualViewport?.removeEventListener('resize', sync);
      window.visualViewport?.removeEventListener('scroll', sync);
    };
  }, [isOpen, isStandalonePwa, skipLock]);

  return isFullscreenModal;
}
